/**
 * BespokeAwaiting — Bespoke-tier awaiting-response confirmation screen.
 *
 * in-app-checkout Phase 3 Session 3.6.
 *
 * Reached from the Bespoke card's "Request Bespoke" CTA on the Membership
 * pricing grid. This is a ONE-WAY REQUEST confirmation — a pending/awaiting
 * state, NOT the green success of a completed subscription, NOT a mailto:.
 *
 * Fidelity target: Compositional A/B (flow + structure) vs the prototype's
 * Flow D (`BespokeAwaiting` function in `apps/web/src/prototypes/in-app-checkout/index.tsx`)
 * + `refs/prototype-fidelity-spec.md § D — Bespoke awaiting-response`.
 * Chrome (typography, color) matches the real Membership screen (ref #0).
 *
 * Two visual states:
 *
 *   1. **Submit state** — rendered when the component mounts (the user hasn't
 *      yet confirmed). Shows the "Request Bespoke access" heading + descriptive
 *      copy + "Request Bespoke" CTA. This gives the user a final confirmation
 *      step before committing.
 *
 *   2. **Awaiting state** — rendered after a successful `POST /api/billing/bespoke-request`.
 *      Shows the `Panel badge="AWAITING RESPONSE"` with warn-tinted mailbox
 *      icon, Membership eyebrow, serif "We've received your request" heading,
 *      body copy, and the "Your account is flagged awaiting Bespoke response"
 *      status box.
 *
 * Error state: inline inline error copy under the CTA (no panel swap — the
 * awaiting panel is the success destination, not the default). Retry is
 * available by pressing the CTA again once the error clears.
 *
 * Token discipline:
 *   - No raw hex.
 *   - Warn tint: `bg-warn-dim border-warn/15 text-warn` (literal classes,
 *     no interpolation — Tailwind v4 no-safelist).
 *
 * Already-requested: if `bespoke_requested_at` is set on the usage row the
 * hub should navigate directly to the awaiting panel, bypassing the submit
 * step. The `initiallySubmitted` prop supports this (pass `true` when the
 * user's profile already has `bespoke_requested_at` set).
 */

import { useState } from 'react';
import { Mailbox } from 'lucide-react';
import { Panel } from '@/components/ui/Panel';
import { Button } from '@/components/ui/Button';
import { useBespokeRequest } from '@/hooks/use-bespoke-request';

// ---------------------------------------------------------------------------
// Membership eyebrow (inline — same token spec as CheckoutTierCard + BillingHub)
// ---------------------------------------------------------------------------

function MembershipEyebrow() {
  return (
    <div className="font-mono text-[11px] font-medium uppercase tracking-[0.22em] text-accent">
      Membership
    </div>
  );
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

export interface BespokeAwaitingProps {
  /**
   * User id passed from the caller (`user?.id` from `useAuth()`).
   * Used by `useBespokeRequest` to scope the cache invalidation — the hook
   * never calls `useAuth()` internally (hook-arg convention).
   */
  userId: string | undefined;
  /**
   * Called when the user clicks "Back to billing" — navigates away from the
   * awaiting-response panel. Typically navigates to `/settings?tab=billing`.
   */
  onDone: () => void;
  /**
   * When `true`, the component skips the submit step and renders the
   * awaiting-response panel immediately. Pass this when the user's profile
   * already has `bespoke_requested_at` set (i.e. a prior visit already
   * triggered the request).
   *
   * @default false
   */
  initiallySubmitted?: boolean;
}

export function BespokeAwaiting({
  userId,
  onDone,
  initiallySubmitted = false,
}: BespokeAwaitingProps) {
  const [isSubmitted, setIsSubmitted] = useState(initiallySubmitted);
  const mutation = useBespokeRequest(userId);

  function handleRequest() {
    mutation.mutate(undefined, {
      onSuccess: () => {
        setIsSubmitted(true);
      },
    });
  }

  // ── Awaiting-response panel (post-submit) ─────────────────────────────────

  if (isSubmitted) {
    return (
      <div data-testid="bespoke-awaiting">
        {/* Warn/neutral tone — pending request, not the green "active" of a
            completed subscription. Badge copy mirrors the prototype fidelity spec. */}
        <Panel title="Bespoke request" badge="AWAITING RESPONSE">
          <div className="flex flex-col items-center gap-4 py-6 text-center">
            {/* Warn-tinted mailbox icon */}
            <span
              data-testid="bespoke-awaiting-icon"
              className="flex h-12 w-12 items-center justify-center rounded-full border border-warn/30 bg-warn-dim text-warn"
            >
              <Mailbox size={24} />
            </span>

            <div className="flex flex-col items-center gap-1.5">
              <MembershipEyebrow />
              <span
                data-testid="bespoke-awaiting-heading"
                className="font-serif text-[22px] font-semibold tracking-[-0.01em] text-text-bright"
              >
                We&rsquo;ve received your request
              </span>
              <span className="max-w-[420px] font-body text-xs leading-relaxed text-text-muted">
                Thanks for your interest in a Bespoke engagement. Our team will
                be in touch shortly to scope an arrangement built around your
                firm. No payment is due &mdash; there&rsquo;s nothing more you
                need to do right now.
              </span>
            </div>

            {/* Status box — warn-tinted, mirrors prototype fidelity spec §D zone 3 */}
            <div
              data-testid="bespoke-awaiting-status-box"
              className="w-full max-w-[440px] rounded-[3px] border border-warn/15 bg-warn-dim px-3.5 py-3 text-left"
            >
              <span className="block font-mono text-[8px] font-semibold uppercase tracking-[1.4px] text-warn">
                Status
              </span>
              <span className="mt-1.5 block font-body text-[11px] leading-relaxed text-text-muted">
                Your account is flagged{' '}
                <span className="text-warn">awaiting Bespoke response</span>.
                Your current membership is unchanged in the meantime &mdash;
                this is a one-way request, not a plan change.
              </span>
            </div>

            {/* Back to billing — outline CTA (mirrors AccentCta outline in prototype) */}
            <div className="flex w-full max-w-[440px] flex-col gap-2">
              <Button
                variant="secondary"
                onClick={onDone}
                data-testid="bespoke-awaiting-done"
                className="w-full"
              >
                Back to billing
              </Button>
            </div>
          </div>
        </Panel>
      </div>
    );
  }

  // ── Submit step (pre-submit) ───────────────────────────────────────────────

  return (
    <div data-testid="bespoke-submit">
      <Panel title="Bespoke request">
        <div className="flex flex-col items-center gap-4 py-6 text-center">
          {/* Warn-tinted mailbox icon — same pending visual language */}
          <span
            data-testid="bespoke-submit-icon"
            className="flex h-12 w-12 items-center justify-center rounded-full border border-warn/30 bg-warn-dim text-warn"
          >
            <Mailbox size={24} />
          </span>

          <div className="flex flex-col items-center gap-1.5">
            <MembershipEyebrow />
            <span
              data-testid="bespoke-submit-heading"
              className="font-serif text-[22px] font-semibold tracking-[-0.01em] text-text-bright"
            >
              Request Bespoke access
            </span>
            <span className="max-w-[420px] font-body text-xs leading-relaxed text-text-muted">
              Bespoke is a customized engagement sized to your firm&rsquo;s
              specific workflow. Requesting will notify our team, who will be in
              touch to scope the arrangement. No payment is due &mdash; your
              current membership is unchanged.
            </span>
          </div>

          {/* What-happens info box (neutral/info tone, not warn — this is a
              pre-submit clarification, not a pending-confirmation state) */}
          <div
            data-testid="bespoke-submit-info-box"
            className="w-full max-w-[440px] rounded-[3px] border border-panel-border bg-panel-bg px-3.5 py-3 text-left"
          >
            <span className="block font-mono text-[8px] font-semibold uppercase tracking-[1.4px] text-text-muted">
              What happens next
            </span>
            <span className="mt-1.5 block font-body text-[11px] leading-relaxed text-text-muted">
              Clicking &ldquo;Request Bespoke&rdquo; notifies the Arcanum team
              with your account details. This is a one-way request &mdash; NOT a
              plan change. Your current subscription and access continue
              unchanged.
            </span>
          </div>

          {/* Error state (inline under the info box) */}
          {mutation.isError && (
            <div
              role="alert"
              data-testid="bespoke-submit-error"
              className="w-full max-w-[440px] rounded-[3px] border border-danger/20 bg-danger-dim px-3.5 py-2.5 text-left font-mono text-xs text-danger"
            >
              {mutation.error instanceof Error
                ? mutation.error.message
                : 'Something went wrong. Please try again.'}
            </div>
          )}

          {/* CTA + back row */}
          <div className="flex w-full max-w-[440px] flex-col gap-2">
            <Button
              variant="primary"
              onClick={handleRequest}
              disabled={mutation.isPending}
              data-testid="bespoke-submit-cta"
              className="w-full"
            >
              {mutation.isPending ? 'Sending…' : 'Request Bespoke'}
            </Button>
            <Button
              variant="ghost"
              onClick={onDone}
              data-testid="bespoke-submit-back"
              className="w-full"
            >
              Back to billing
            </Button>
          </div>
        </div>
      </Panel>
    </div>
  );
}
